import * as React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';
import { useTheme } from '../../../../theming/useTheme';
import { footerSectionPadding } from './footer.utils';
import { VrLineHeight } from '../../../../theming/verticalRhythm/verticalRhythm';
import { TextLinkComponent, ThemedLinkStyle, generateLinkStyleFromTheme } from '../../../partials/link/TextLink';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';

const componentConfigurationKey = 'footerLinksSection';

export interface FooterLinksSectionBaseProps {
  title: string,
  links: {
    text: string,
    href: string
  }[]
}

interface LinksSectionThemeProps {
  color: string
  vrLineHeight: VrLineHeight
  idleColor: string
  activeStateColor: string
  hoverStateColor: string
}

type LinksSectionProps = FooterLinksSectionBaseProps & ThemedLinkStyle & LinksSectionThemeProps;

const getStyle = (theme: LinksSectionThemeProps) => {
  const { vrLineHeight } = theme;
  const padding = footerSectionPadding(vrLineHeight);
  return StyleSheet.create({
    section: {
      ...padding,
    },
    title: {
      textAlign: 'center',
      color: theme.color,
    },
    gridWrapper: {
      display: 'flex',
      justifyContent: 'center',
    },
    linksWrapper: {
      display: 'inline-grid',
      [MEDIA_BREAKPOINTS.X_LARGE]: {
        gridTemplateColumns: 'auto auto auto auto',
        gridGap: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 4}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.X_LARGE] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.LARGE]: {
        gridTemplateColumns: 'auto auto auto auto',
        gridGap: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 4}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        gridTemplateColumns: 'auto auto auto',
        gridGap: `. ${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 4}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        gridTemplateColumns: 'auto auto',
        gridGap: `0 ${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 2}px`,
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      },
    },
  });
};

export const FooterLinksSectionComponent: React.FunctionComponent<LinksSectionProps> = props => {
  const { title, links } = props;
  const style = getStyle(props);
  const linkStyle = generateLinkStyleFromTheme({
    ...props,
  });

  return (
    <div className={css(style.section)}>
      <h4 className={css(style.title)}>{title}</h4>
      <div className={css(style.gridWrapper)}>
        <div className={css(style.linksWrapper)}>
          {links.map((link, index) => <TextLinkComponent key={index} href={link.href} linkStyle={linkStyle}><p>{link.text}</p></TextLinkComponent>)}
        </div>
      </div>
    </div>
  );
};

const mapThemeToProps = (theme: LinksSectionThemeProps) => ({
  color: theme.color,
  vrLineHeight: theme.vrLineHeight,
  idleColor: theme.idleColor,
  activeStateColor: theme.activeStateColor,
  hoverStateColor: theme.hoverStateColor,
});

export const FooterLinksSection = (props: FooterLinksSectionBaseProps) => {
  const themeContext = useTheme(componentConfigurationKey, mapThemeToProps);
  return <FooterLinksSectionComponent {...themeContext} {...props} />;
};
